/**
 * Firm-wide calendar data as TanStack queries: the firm's reminders and
 * teammates' synced Google/Outlook events for a visible window. Both are
 * persisted so the calendar paints from disk, revalidate in the background,
 * and refetch (debounced) when activity lands on the stream.
 */
import dayjs from "dayjs";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  CalendarEventsAPI,
  type CalendarEventsResponse,
  type CalendarSource
} from "@/api/calendarEvents";
import {
  LawFirmRemindersAPI,
  type LawFirmReminder
} from "@/api/lawFirmReminders";
import { queryClient } from "@/lib/query/queryClient";
import { useRealtimeEvent } from "@/lib/realtime/RealtimeProvider";

import {
  mergeEvents,
  toCalendarEvents,
  toExternalEvents,
  type CalendarEvent,
  type CalendarView
} from "./calendarModel";

export const CALENDAR_REFRESH_DEBOUNCE_MS = 2000;

/** A visible window as `YYYY-MM-DD` day keys (inclusive). */
export type DayRange = { from: string; to: string };

export const firmRemindersKey = (range: DayRange, assignee: string | null) =>
  ["firm-reminders", range.from, range.to, assignee] as const;

export const calendarEventsKey = (range: DayRange) =>
  ["calendar-events", range.from, range.to] as const;

/** The days a view shows around `anchor` (month views include grid overflow). */
export const rangeForView = (view: CalendarView, anchor: Date): DayRange => {
  const a = dayjs(anchor);
  switch (view) {
    case "month": {
      const start = a.startOf("month").startOf("week");
      const end = a.endOf("month").endOf("week");
      return { from: start.format("YYYY-MM-DD"), to: end.format("YYYY-MM-DD") };
    }
    case "week":
      return {
        from: a.startOf("week").format("YYYY-MM-DD"),
        to: a.endOf("week").format("YYYY-MM-DD")
      };
    case "agenda":
      return {
        from: a.format("YYYY-MM-DD"),
        to: a.add(30, "day").format("YYYY-MM-DD")
      };
    case "day":
    default:
      return { from: a.format("YYYY-MM-DD"), to: a.format("YYYY-MM-DD") };
  }
};

/** Debounced invalidation of the calendar queries when the firm's data moves. */
const useCalendarInvalidation = () => {
  const timer = useRef<number | null>(null);
  const schedule = useCallback(() => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      timer.current = null;
      void queryClient.invalidateQueries({ queryKey: ["firm-reminders"] });
      void queryClient.invalidateQueries({ queryKey: ["calendar-events"] });
    }, CALENDAR_REFRESH_DEBOUNCE_MS);
  }, []);
  useEffect(
    () => () => {
      if (timer.current) window.clearTimeout(timer.current);
    },
    []
  );
  useRealtimeEvent("activity.created", schedule);
};

export type FirmRemindersState = {
  reminders: LawFirmReminder[];
  events: CalendarEvent[];
  isLoading: boolean;
  isFetching: boolean;
  error: string | null;
  reload: () => Promise<unknown>;
};

/** Firm reminders in `range`, optionally only `assignee`'s, as calendar events. */
export const useFirmReminders = (
  range: DayRange,
  {
    assignee = null,
    enabled = true
  }: { assignee?: string | null; enabled?: boolean } = {}
): FirmRemindersState => {
  useCalendarInvalidation();
  const query = useQuery(
    {
      queryKey: firmRemindersKey(range, assignee),
      queryFn: () =>
        LawFirmRemindersAPI.list({
          from: range.from,
          to: range.to,
          ...(assignee ? { assignee } : {})
        }),
      enabled,
      meta: { persist: true }
    },
    queryClient
  );
  const reminders = useMemo(() => query.data ?? [], [query.data]);
  const events = useMemo(() => {
    const names: Record<string, string> = {};
    for (const r of reminders) {
      if (r.client_name && r.foreign_national_id)
        names[r.foreign_national_id] = r.client_name;
    }
    return toCalendarEvents(reminders, names);
  }, [reminders]);
  return {
    reminders,
    events,
    isLoading: enabled && query.isPending && !query.data,
    isFetching: query.isFetching,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : "Couldn't load reminders."
      : null,
    reload: () => query.refetch()
  };
};

export type CalendarEventsState = {
  events: CalendarEvent[];
  sources: CalendarSource[];
  syncedAt: string | null;
  isLoading: boolean;
  isFetching: boolean;
  /** A forced provider refresh is running. */
  isRefreshing: boolean;
  error: string | null;
  /** Pull from the providers again (rate limited server-side). */
  refresh: () => Promise<void>;
};

/** Teammates' synced events in `range`. */
export const useCalendarEvents = (
  range: DayRange,
  { enabled = true }: { enabled?: boolean } = {}
): CalendarEventsState => {
  const [forcing, setForcing] = useState(false);
  const key = calendarEventsKey(range);
  const query = useQuery(
    {
      queryKey: key,
      queryFn: () => CalendarEventsAPI.list({ from: range.from, to: range.to }),
      enabled,
      meta: { persist: true }
    },
    queryClient
  );
  const { data } = query;
  const events = useMemo(
    () => (data ? toExternalEvents(data.events) : []),
    [data]
  );
  const refresh = useCallback(async () => {
    setForcing(true);
    try {
      const next = await CalendarEventsAPI.list({
        from: range.from,
        to: range.to,
        refresh: true
      });
      queryClient.setQueryData(calendarEventsKey(range), next);
    } finally {
      setForcing(false);
    }
  }, [range]);
  return {
    events,
    sources: data?.sources ?? [],
    syncedAt: data?.synced_at ?? null,
    isLoading: enabled && query.isPending && !data,
    isFetching: query.isFetching,
    isRefreshing: forcing,
    error: query.error
      ? query.error instanceof Error
        ? query.error.message
        : "Couldn't load synced calendars."
      : null,
    refresh
  };
};

/** Reminders plus synced events, merged and sorted. */
export const useMergedCalendar = (
  reminders: CalendarEvent[],
  external: CalendarEvent[],
  { showReminders = true, showExternal = true } = {}
): CalendarEvent[] =>
  useMemo(
    () =>
      mergeEvents(showReminders ? reminders : [], showExternal ? external : []),
    [reminders, external, showReminders, showExternal]
  );
